import { useAnalytics, getDatabaseContainerType } from '@databases/analytics';
import { useDialog } from '@databases/dialog';
import { getFieldType } from '@databases/fields';
import { messages } from '@databases/intl/messages';
import { getTextDelta } from '@databases/react/useTextDelta';
import { defaultStoreUtils } from '@databases/redux';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { type DatabaseFieldType } from '@databases/types/database';
import { DatabaseChangeType } from '@databases/types/redux';
import { isEmptyValue } from '@databases/utils/database/field-types';
import {
  getConvertedDatabaseValue,
  getDatabaseFieldRequired,
  getExistingFieldNames
} from '@databases/utils/database/utils';
import { getUniqueFieldName } from '@databases/utils/field-name';
import { useIsFeatureEnabled } from '@databases/utils/hooks/useIsFeatureEnabled';
import React, { type FunctionComponent, useCallback } from 'react';
import { useIntl } from 'react-intl-next';

import FieldTypeDropdown from './FieldTypeDropdown.tsx';

interface FieldTypeEditProps {
  contentId: string;
  fieldId: string;
  shouldFitContainer?: boolean;
  hasFieldTypeError: boolean;
  ariaLabelledBy?: string;
}

type UseChangeFieldTypeProps = Pick<FieldTypeEditProps, 'contentId' | 'fieldId'>;

export const useChangeFieldType = ({ contentId, fieldId }: UseChangeFieldTypeProps) => {
  const client = useAnalytics();
  const { showDialog } = useDialog();

  const [field, isFieldEmpty, database] = defaultStoreUtils.useEqualSelector((state) => {
    const database = selectDatabaseDraftByContentId(state, {
      contentId,
      includeDeleted: true
    });
    const field = getDatabaseFieldRequired(database, fieldId);
    const entries = Object.values(database.entriesById);

    const isFieldEmpty = entries.every((entry) => {
      const value = getConvertedDatabaseValue(database, field, entry, state);
      return isEmptyValue({ database, field, entry, value }, state);
    });

    return [field, isFieldEmpty, database] as const;
  });

  const { formatMessage } = useIntl();
  const dispatch = defaultStoreUtils.useDispatch();

  const getFormattedFieldLabel = useCallback(
    (fieldType: DatabaseFieldType) =>
      formatMessage(messages['field-settings.field-name-inline-edit-default-text'], {
        defaultFieldName: formatMessage(getFieldType(fieldType).label)
      }),
    [formatMessage]
  );

  const updateFieldNameIfAutogenerated = useCallback(
    (newFieldType: DatabaseFieldType) => {
      const oldDefaultFieldLabel = getFormattedFieldLabel(field.type);
      if (field.name.includes(oldDefaultFieldLabel)) {
        const newDefaultLabel = getFormattedFieldLabel(newFieldType);
        const newName = field.name.replace(oldDefaultFieldLabel, newDefaultLabel);
        const uniqueFieldName = getUniqueFieldName(
          newName,
          getExistingFieldNames(database, field)
        );
        dispatch(
          addChangeToDraft(contentId, {
            type: DatabaseChangeType.SET_FIELD_NAME,
            fieldId,
            name: uniqueFieldName,
            delta: getTextDelta(field.name, uniqueFieldName, field.name.length)
          })
        );
      }
    },
    [contentId, database, dispatch, field, fieldId, getFormattedFieldLabel]
  );

  const handleChange = useCallback(
    (val: DatabaseFieldType) => {
      const sendFieldUpdatedEvent = () => {
        client.sendDatabaseFieldOptionUpdatedEvent({
          source: 'fieldSettingsDialog',
          contentId: contentId,
          databaseContainer: getDatabaseContainerType(),
          fieldSelection: val,
          isNewField: false
        });
      };

      if (isFieldEmpty) {
        updateFieldNameIfAutogenerated(val);
        dispatch(
          addChangeToDraft(contentId, {
            type: DatabaseChangeType.SET_FIELD_TYPE,
            fieldId,
            fieldType: val
          })
        );
        sendFieldUpdatedEvent();
      } else {
        showDialog('ConfirmDialog', {
          heading: formatMessage(messages['field-settings.change-field-heading']),
          body: <>{formatMessage(messages['field-settings.change-field-dialog'])}</>,
          yesLabel: formatMessage(messages['field-settings.change-field-button']),
          noLabel: formatMessage(messages['field-settings.change-field-cancel-button']),
          appearance: 'warning',
          onClose: (result) => {
            if (result) {
              updateFieldNameIfAutogenerated(val);
              dispatch(
                addChangeToDraft(contentId, {
                  type: DatabaseChangeType.SET_FIELD_TYPE,
                  fieldId,
                  fieldType: val
                })
              );
              sendFieldUpdatedEvent();
            }
          }
        });
      }
    },
    [
      isFieldEmpty,
      client,
      contentId,
      updateFieldNameIfAutogenerated,
      dispatch,
      fieldId,
      showDialog,
      formatMessage
    ]
  );

  return {
    field,
    changeFieldType: handleChange
  };
};

const FieldTypeEdit: FunctionComponent<FieldTypeEditProps> = ({
  contentId,
  fieldId,
  shouldFitContainer,
  hasFieldTypeError,
  ariaLabelledBy
}) => {
  const { formatMessage } = useIntl();
  const { field, changeFieldType } = useChangeFieldType({ contentId, fieldId });
  const a11yFeatureGate = useIsFeatureEnabled('a11yWhiteboardsChanges');

  return (
    <FieldTypeDropdown
      shouldFitContainer={shouldFitContainer}
      value={field.type}
      onChange={changeFieldType}
      hasFieldTypeError={hasFieldTypeError}
      label={a11yFeatureGate ? formatMessage(messages['field-settings.type']) : undefined}
      ariaLabelledBy={ariaLabelledBy}
    />
  );
};

export default FieldTypeEdit;
